import { ApiClientError } from '@/lib/api/client'
import { landingApiRequest } from '@/lib/landing-api'
import type { ScratchCardState } from '@/lib/campaign-scratch-card'
import type { AchievementRewards } from '@/lib/campaign-gamification'
import { MONTHLY_MEDALS, type MonthlyMedalId } from '@/lib/monthly-medals'

export type LandingAchievementId =
  | 'first_purchase'
  | 'frequent_supporter'
  | 'special_supporter'
  | 'profile_complete'
  | 'avatar_customized'
  | 'scratch_card'
  | 'roulette'
  | 'calendar'

export function achievementScope(id: LandingAchievementId): 'account' | 'campaign' {
  return id === 'profile_complete' || id === 'avatar_customized' ? 'account' : 'campaign'
}

export type CalendarAttendance = {
  eligible: boolean
  timezone: string
  today: string
  target_days: number
  consecutive_days: number
  started_on: string | null
  last_present_on: string | null
  completed_at: string | null
  checked_in_today: boolean
  new_check_in: boolean
}

export type LandingAchievement = {
  scope?: 'account' | 'campaign'
  rewards?: AchievementRewards
  reward_awarded_at?: string
  id: LandingAchievementId
  unlocked: boolean
  unlocked_at: string | null
  current_value: string
  target_value: string
  locked_reason?: string | null
  content: { title: string; text: string | null; video_url: string | null } | null
}

export type LandingCampaignAchievements = {
  monthly_medals?: MonthlyMedalAchievement[]
  rewards_changed?: boolean
  rewards_revision?: number
  campaign_id: number
  summary: {
    paid_purchase_count: number
    total_paid: string
    highest_purchase_amount: string
  }
  achievements: LandingAchievement[]
  scratch_card?: ScratchCardState
  calendar?: CalendarAttendance | null
}

export type MonthlyMedalAchievement = Omit<LandingAchievement, 'id'> & {
  id: MonthlyMedalId
  period: string
}

const ACHIEVEMENT_IDS: LandingAchievementId[] = [
  'first_purchase',
  'frequent_supporter',
  'special_supporter',
  'profile_complete',
  'avatar_customized',
  'scratch_card',
  'roulette',
  'calendar',
]

export async function landingFetchCampaignAchievements(
  campaignId: number
): Promise<LandingCampaignAchievements> {
  if (!Number.isInteger(campaignId) || campaignId <= 0) {
    throw new ApiClientError('Não foi possível carregar suas conquistas.', 400)
  }

  const payload = await landingApiRequest<LandingCampaignAchievements>(
    `/api/me/campaigns/${campaignId}/achievements/synchronize`,
    { method: 'POST' }
  )

  return normalizeCampaignAchievements(payload)
}

export function normalizeCampaignAchievements(
  payload: LandingCampaignAchievements
): LandingCampaignAchievements {
  const campaignId = Number(payload.campaign_id)
  const achievements = Array.isArray(payload.achievements) ? payload.achievements : []

  return {
    monthly_medals: MONTHLY_MEDALS.flatMap(({ id }) => {
      const item = Array.isArray(payload.monthly_medals)
        ? payload.monthly_medals.find((item) => item.id === id)
        : undefined
      if (
        !item ||
        !/^\d{4}-(0[1-9]|1[0-2])$/.test(item.period) ||
        item.period.slice(5) !== id.slice(-2)
      )
        return []
      return [
        {
          ...item,
          id,
          unlocked: item.unlocked === true,
          current_value: normalizeAmount(item.current_value),
          rewards: item.rewards,
          content:
            item.unlocked && item.content
              ? {
                  ...item.content,
                  video_url: safeYoutubeEmbedUrl(item.content.video_url),
                }
              : null,
        },
      ]
    }),
    campaign_id: Number.isInteger(campaignId) && campaignId > 0 ? campaignId : 0,
    scratch_card: payload.scratch_card,
    calendar: payload.calendar,
    rewards_changed: payload.rewards_changed === true,
    rewards_revision: Number(payload.rewards_revision) || 0,
    summary: {
      paid_purchase_count: Math.max(0, Number(payload.summary?.paid_purchase_count) || 0),
      total_paid: normalizeAmount(payload.summary?.total_paid),
      highest_purchase_amount: normalizeAmount(payload.summary?.highest_purchase_amount),
    },
    achievements: ACHIEVEMENT_IDS.filter((id) =>
      achievements.some((item) => item.id === id)
    ).map((id) => {
      const item = achievements.find((achievement) => achievement.id === id)
      return {
        id,
        scope: achievementScope(id),
        rewards: item?.rewards,
        reward_awarded_at: item?.reward_awarded_at,
        unlocked: item?.unlocked === true,
        unlocked_at: typeof item?.unlocked_at === 'string' ? item.unlocked_at : null,
        current_value: normalizeAmount(item?.current_value),
        target_value: normalizeAmount(item?.target_value),
        locked_reason:
          item?.unlocked === false && typeof item.locked_reason === 'string'
            ? item.locked_reason.trim() || null
            : null,
        content:
          item?.unlocked === true &&
          item.content &&
          typeof item.content.title === 'string'
            ? {
                title: item.content.title,
                text: typeof item.content.text === 'string' ? item.content.text : null,
                video_url: safeYoutubeEmbedUrl(item.content.video_url),
              }
            : null,
      }
    }),
  }
}

function safeYoutubeEmbedUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  return /^https:\/\/www\.youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]{11}$/.test(value)
    ? value
    : null
}

function normalizeAmount(value: unknown): string {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : '0'
}
